import React, {
  forwardRef,
  ForwardRefExoticComponent,
  RefAttributes,
  useEffect,
  useRef,
  useState,
} from 'react';
import {
  Input as AntInput,
  InputProps as AntInputProps,
} from '@vertem-core/vertice-ds';
import MaskedInput from 'antd-mask-input';
import { InputProps } from 'antd/lib/input';
import { EyeInvisibleOutlined, EyeOutlined } from '@ant-design/icons';
import { Spin } from 'antd';

// ============================================================================
// ESTILOS
// ============================================================================
import {
  InputWrapper,
  StyledLabel,
  StyledInput,
  StyledMaskedInput,
  LoaderWrapper,
  SpinStyle,
} from './Input.styles';

// ============================================================================
// TIPOS E INTERFACES
// ============================================================================

interface CustomInputProps
  extends Omit<AntInputProps, 'defaultValue' | 'showCount'> {
  mask?: string;
  customDefaultValue?: string;
  value?: string;
  placeholder?: any;
  disabled?: boolean;
  type?: string;
  loading?: boolean;
  shouldApplyMask?: boolean;
}

interface InputComponent
  extends ForwardRefExoticComponent<CustomInputProps & RefAttributes<Node>> {
  Password: typeof Password;
}

// ============================================================================
// COMPONENTE INPUT
// ============================================================================

const Input = forwardRef<HTMLInputElement, CustomInputProps>(
  (props: any, ref: any) => {
    const [isFocused, setIsFocused] = useState(false);
    const [hasValue, setHasValue] = useState(!!props.value);
    const [dynamicMask, setDynamicMask] = useState('');
    const [shouldApplyMask, setShouldApplyMask] = useState(
      props.shouldApplyMask,
    );
    const [inputType, setInputType] = useState(
      props.type === 'date' ? 'text' : props.type,
    );
    const inputRef = useRef<HTMLInputElement>(null) as any;

    useEffect(() => {
      const unmaskedValue = props.value;
      setHasValue(!!unmaskedValue);

      if (props.type === 'document') {
        if (
          unmaskedValue &&
          unmaskedValue.replace(/_/g, '').replace(/ /g, '').length > 15
        ) {
          setDynamicMask('00.000.000/000-00');
        } else {
          setDynamicMask('000.000.000-0000');
        }
      }

      if (props.disabled) {
        setIsFocused(true);
      }

      if (props.type === 'date' && unmaskedValue) {
        setInputType('date');
      }

      if ((props.mask || props.type === 'document') && unmaskedValue) {
        setShouldApplyMask(true);
      }
    }, [props.value, props.type]);

    useEffect(() => {
      if (shouldApplyMask && inputRef.current) {
        inputRef.current.focus();
      }
    }, [shouldApplyMask]);

    const handleFocus = () => {
      setIsFocused(true);
      setShouldApplyMask(true);

      if (props.type === 'date') {
        setInputType('date');
      }
    };

    const handleBlur = (e: any) => {
      setIsFocused(false);

      if (props.type === 'date') {
        setInputType(e.target.value ? 'date' : 'text');
        setHasValue(!!e.target.value);
      }
    };

    const renderLoader = () => {
      if (!props.loading) return null;

      return (
        <LoaderWrapper>
          <Spin size="small" style={SpinStyle} />
        </LoaderWrapper>
      );
    };

    return (
      <InputWrapper>
        <StyledLabel $isFocused={isFocused} $hasValue={hasValue}>
          {props.placeholder}
        </StyledLabel>

        {props.type !== 'date' &&
        (props.mask || props.type === 'document') &&
        shouldApplyMask ? (
          <>
            <StyledMaskedInput
              {...props}
              ref={(node) => {
                if (typeof ref === 'function') ref(node);
                else if (ref) ref.current = node;
                inputRef.current = node;
              }}
              maskOptions={{
                placeholderChar: ' ',
              }}
              mask={props.mask || dynamicMask}
              isFocused={isFocused || hasValue}
              placeholder={' '}
              autoComplete="off"
              onFocus={handleFocus}
              onBlur={handleBlur}
            />
            {renderLoader()}
          </>
        ) : (
          <>
            <StyledInput
              {...props}
              type={inputType}
              ref={(node: any) => {
                if (typeof ref === 'function') ref(node);
                else if (ref) ref.current = node;
                inputRef.current = node;
              }}
              autoComplete="off"
              placeholder={' '}
              onFocus={handleFocus}
              onBlur={handleBlur}
            />
            {renderLoader()}
          </>
        )}
      </InputWrapper>
    );
  },
) as InputComponent;

Input.displayName = 'Input';

// ============================================================================
// COMPONENTE PASSWORD
// ============================================================================

const Password = forwardRef<Node, InputProps>((props, _ref) => {
  const [visible, setVisible] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [hasValue, setHasValue] = useState(false);

  useEffect(() => {
    const hasInputValue = props.value !== undefined && props.value !== '';
    setHasValue(hasInputValue);
  }, [props.value]);

  return (
    <InputWrapper>
      <StyledLabel $isFocused={isFocused} $hasValue={hasValue}>
        {props.placeholder}
      </StyledLabel>
      <StyledInput
        {...props}
        type={visible ? 'text' : 'password'}
        name="unique-password-field"
        id="unique-password-field"
        placeholder={undefined}
        suffix={
          visible ? (
            <EyeOutlined onClick={() => setVisible(false)} />
          ) : (
            <EyeInvisibleOutlined onClick={() => setVisible(true)} />
          )
        }
        onFocus={() => setIsFocused(true)}
        onBlur={(e: any) => {
          setIsFocused(false);
          setHasValue(!!e.target.value);
        }}
      />
    </InputWrapper>
  );
});

Password.displayName = 'Input.LoginPassword';

Input.Password = Password;

export default Input;
